/**
 * Maps Redux user state to Apollo NavigationBar profileMenuProps.
 * Replaces legacy UserMenu dropdown (profile, User Guides, Support, log out).
 */
import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import FileIcon from 'apollo-react-icons/File';
import SettingsIcon from 'apollo-react-icons/Cog';
import Avatar from 'apollo-react/components/Avatar';
import Box from 'apollo-react/components/Box';
import {
    getUsername,
    getPreferredUsername,
    currentUserInfo,
    getUserInfo,
    getCurrentActiveRole,
    userCurrentEnterprise,
} from 'spark-state/common/user/userReducer';
import { getMappedEnterpriseData } from 'spark-state/common/app/reducer';
import { getVersionNumber } from 'spark-state/enterprise/enterpriseReducer';
import { USER_GUIDES_BASE_PATH } from 'spark-views/userGuides/UserGuidesRoute';
import { buildSupportLink, openExternalLink } from '../supportLinkUtils';

const getInitials = (username) => {
    const nameArray = username.split(' ');
    const firstName = nameArray[0];
    let initials = firstName.slice(0, 1) ? firstName.slice(0, 1).toUpperCase() : '';
    if (nameArray.length > 1) {
        const lastName = nameArray.pop();
        initials = initials.concat(lastName.slice(0, 1).toUpperCase());
    }
    return initials;
};

const ProfileVersionFooter = () => {
    // Apollo profile menu footer; footer bar also shows version link when available (SAF-19125).
    const version = useSelector((state) => getVersionNumber(state));

    if (!version) {
        return null;
    }

    const displayVersion = version.startsWith('v') ? version : `v${version}`;

    return (
        <Box
            sx={{
                textAlign: 'center',
                color: '#8C8C8C',
                fontSize: '12px',
                marginTop: '8px',
                marginBottom: '8px',
            }}
        >
            {displayVersion}
        </Box>
    );
};

export function useSparkHeaderProfile({ logOut }) {
    const location = useLocation();
    const name = useSelector((state) => getUsername(state) || getPreferredUsername(state));
    const email = useSelector((state) => currentUserInfo(state).email);
    const user = useSelector(getUserInfo);
    const mappedEnterprise = useSelector(getMappedEnterpriseData);
    const enterpriseId = useSelector(userCurrentEnterprise);
    const currentActiveRole = useSelector(getCurrentActiveRole);
    const selectedEnterprise =
        mappedEnterprise &&
        mappedEnterprise.find((enterprise) => enterprise.entId === parseInt(enterpriseId, 10));
    const availableRoles = (selectedEnterprise && selectedEnterprise.roles) || [];
    const roleTitle =
        availableRoles.find((role) => role.roleVal === currentActiveRole)?.roleDisplayVal || '';
    const initials = useMemo(() => getInitials(name || ''), [name]);

    const openSupport = useCallback(() => {
        openExternalLink(buildSupportLink(user, mappedEnterprise, location.pathname));
    }, [user, mappedEnterprise, location.pathname]);

    const openUserGuides = useCallback(() => {
        openExternalLink(USER_GUIDES_BASE_PATH);
    }, []);

    return useMemo(
        () => ({
            menuItems: [
                { text: 'User Guides', icon: FileIcon, onClick: openUserGuides },
                { text: 'Support', icon: SettingsIcon, onClick: openSupport },
            ],
            title: roleTitle,
            name,
            email,
            logoutText: 'Log out',
            icon: (
                <Avatar size="small" alt={name} stateless>
                    {initials}
                </Avatar>
            ),
            logoutButtonProps: { onClick: logOut },
            extraContent: <ProfileVersionFooter />,
        }),
        [roleTitle, name, email, initials, logOut, openSupport, openUserGuides]
    );
}
